/* ============
 * Account Store
 * ============
 */

import { defineStore } from 'pinia';
import './pinia';
import { useCompanyStore } from './company';
import { useNotifierStore } from './notifier';

/**
 * SP-13795 — single source of truth for the "temp agency mode" company flag.
 *
 * Two shapes have to be tolerated:
 *  - `account.company` is run through `transformers/company.js` → camelCase
 *    `isTempAgencyMode`. This is the company OWNING the account (the parent).
 *  - `company.currentCompany` is the company currently selected in the UI. When a
 *    child company is active it comes straight from `account.company.children`,
 *    which the transformer passes through untouched → snake_case
 *    `is_temp_agency_mode` (and the key may simply be absent).
 *
 * The flag is a group-level attribute in practice (it already drives the whole
 * Talent Work branding from the parent in `plugins/brand.js`), so both are OR-ed:
 * a child that does not carry the key still inherits its parent's mode.
 *
 * SP-13832 (Sentry CUSTOMER-59X) — every read is null-safe: `company` stays
 * `null` until `GET /me` resolves (dispatched from `app.vue` mounted(), i.e.
 * after already-routed components did their first render), and the company
 * store may not be hydrated yet either. Templates must go through this getter
 * instead of reading `accountStore.company.isTempAgencyMode` directly.
 */
const readTempAgencyMode = (company) => Boolean(
  company && (company.isTempAgencyMode || company.is_temp_agency_mode),
);

export const useAccountStore = defineStore('account', {
  state: () => ({
    id: null,
    email: null,
    firstName: null,
    lastName: null,
    links: null,
    salesManager: null,
    operationalManager: null,
    kpi: null,
    phone: null,
    company: null,
    companies: [],
    canValidateOrder: null,
    mainPicture: null,
    create: {
      userInfo: null,
      userCompany: null,
      lists: null,
    },
    permissions: null,
    paymentStatus: null,
    orderBlocked: false,
    admin: false,
    jobPosition: null,
    arcadeLinks: null,
    newsletterSubscribed: null,
  }),

  getters: {
    isTempAgencyMode() {
      return readTempAgencyMode(this.company)
        || readTempAgencyMode(useCompanyStore().currentCompany);
    },
    mainCompanyId() {
      return this.companies.length ? this.companies[0].id : (this.company && this.company.id);
    },
  },

  actions: {
    store(account) {
      this.id = account.id;
      this.email = account.email;
      this.firstName = account.firstName;
      this.lastName = account.lastName;
      this.links = account.links;
      this.company = account.company;
      this.companies = account.companies || [];
      this.operationalManager = account.operationalManager;
      this.salesManager = account.salesManager;
      this.paymentStatus = account.paymentStatus;
      this.orderBlocked = account.orderBlocked;
      this.kpi = account.kpi;
      this.phone = account.phone;
      this.admin = account.admin;
      this.canValidateOrder = account.canValidateOrder;
      this.mainPicture = account.mainPicture;
      this.jobPosition = account.jobPosition;
      this.arcadeLinks = account.arcadeLinks;
      this.newsletterSubscribed = account.newsletterSubscribed;
      // SP-13795 — mirror the `perms` pattern: the temp-agency flag is needed by the
      // router guard, which runs before `accountService.find()` has resolved on a
      // direct URL access. Persisting it keeps the guard accurate across reloads.
      localStorage.setItem(
        'temp-agency-mode',
        JSON.stringify(Boolean(account.company && account.company.isTempAgencyMode)),
      );
      useCompanyStore().setDefault(account.company);
      if (account.kpi) {
        const { kpi } = account;
        if (kpi.total_package_hours_left > 0) {
          if ((kpi.total_package_hours_left - kpi.total_scheduled_hours) < 0) {
            if (account.salesManager) {
              let message = '';
              if (account.salesManager.phone !== undefined) {
                message = `<strong>Vous avez programmé des missions au delà du nombre de crédits restants</strong>.
Veuillez contacter <strong>${account.salesManager.firstName}</strong> pour régulariser votre situation au <a href="tel:${account.salesManager.phone}"><strong>${account.salesManager.phone}</strong></a>`;
              } else {
                message = `<strong>Vous avez programmé des missions au delà du nombre de crédits restants</strong>.
Veuillez contacter <strong>${account.salesManager.firstName}</strong> pour régulariser votre situation au <a href="tel:0184257482"><strong>01 84 25 74 82</strong></a>`;
              }
              useNotifierStore().open({ message, name: 'no_credit' });
            }

            if (!account.salesManager) {
              const message = '<strong>Vous avez programmé des missions au delà du nombre de crédits restants</strong>. Veuillez nous contacter pour régulariser votre situation au <a href="tel:0184257482"><strong>01 84 25 74 82</strong></a>';
              useNotifierStore().open({ message, name: 'no_credit_no_sales' });
            }
          }
        }
      }
    },

    updateStudentStats(studentStat) {
      if (this.kpi) {
        if (studentStat) {
          this.kpi.count_top_students = studentStat.topStudentsCount;
          this.kpi.count_students = studentStat.countStudents;
        } else {
          this.kpi.count_top_students = 0;
          this.kpi.count_students = 0;
        }
      }
    },

    clear() {
      this.id = null;
      this.email = null;
      this.firstName = null;
      this.lastName = null;
      this.links = null;
      this.company = null;
      this.operationalManager = null;
      this.salesManager = null;
      this.kpi = null;
      this.phone = null;
      this.admin = false;
      this.canValidateOrder = null;
      this.create.userInfo = null;
      // NOTE: the Vuex mutation also cleared `create.company`, a key that never
      // existed on the state (the real one is `create.userCompany`) and that no
      // consumer reads. Dropped rather than kept verbatim: under Pinia it would
      // graft an undeclared, non-reactive key onto the state and desynchronise
      // `$reset()`. Resetting `create.userCompany` instead would be a behaviour
      // change and needs product approval — see SP-13864 in the MR description.
      this.permissions = null;
    },

    countQuotesDecrement() {
      if (this.company.countPendingQuote > 0) {
        this.company.countPendingQuote -= 1;
      }
    },

    CreateUserInfo(userInfo) {
      this.create.userInfo = userInfo;
    },

    UpdateUserInfo(account) {
      this.firstName = account.firstname;
      this.lastName = account.lastname;
      this.phone = account.phone;
      this.mainPicture = account.image;
      this.jobPosition = account.post;
    },

    CreateCompanyInfo(companyInfo) {
      this.create.userCompany = companyInfo;
    },

    CreateListChoices(lists) {
      this.create.lists = lists;

      this.create.lists.countries.forEach((country) => {
        country.id = country.code;
      });
    },

    Permissions(perms) {
      localStorage.setItem('perms', JSON.stringify(perms));
      this.permissions = perms;
    },
  },
});

export default useAccountStore;
